/**
 * Pure helpers for the /monitor map's seatback touches: the route drawing
 * itself in, origin/destination pins, the direction-of-travel arrow, dimming
 * everything but the shipment in focus, and the day/night band.
 */

import { haversineKm } from './MapUtils';
import { lngDelta, wrapLngNear } from './monitorRouteShape';
import { toCoordinate } from './needsAttention';

/** How long a spotlight's route takes to draw itself in. */
export const ROUTE_DRAW_MS = 1600;
/** Share of the draw spent on the traveled part; the rest is what's left. */
const ROUTE_DRAW_TRAVELED_SHARE = 0.55;
/** Opacity of every other shipment while one is in focus. */
export const MONITOR_DIM_OPACITY = 0.3;
/** The night band moves ~1.25° in 5 minutes; no need to redraw more often. */
export const NIGHT_REFRESH_MS = 5 * 60 * 1000;
/**
 * Night band strength: the band is the monitor's only day/night signal, so
 * it has to read across a room on the light map, and stay soft on the dark.
 */
export const NIGHT_OPACITY_LIGHT = 0.16;
export const NIGHT_OPACITY_DARK = 0.3;

const easeInOut = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

const clamp01 = (t: number): number => Math.min(1, Math.max(0, t));

/**
 * How much of each route segment is drawn `elapsedMs` into the animation
 * (0 = none, 1 = all). The traveled part draws first, then the remaining part
 * picks up where the shipment is now, like a seatback map.
 */
export const routeDrawProgress = (
  elapsedMs: number
): { traveled: number; remaining: number } => {
  const t = clamp01(elapsedMs / ROUTE_DRAW_MS);
  return {
    traveled: easeInOut(clamp01(t / ROUTE_DRAW_TRAVELED_SHARE)),
    remaining: easeInOut(
      clamp01((t - ROUTE_DRAW_TRAVELED_SHARE) / (1 - ROUTE_DRAW_TRAVELED_SHARE))
    ),
  };
};

/**
 * `line-trim-offset` that shows the first `drawn` share of a line. The trim
 * hides [start, end]; [1, 1] hides nothing.
 */
export const trimOffsetFor = (drawn: number): [number, number] => [
  clamp01(drawn),
  1,
];

/**
 * The dashed part of the monitor's line: dash and gap, in line widths.
 * Square ends: round ends on dashes this short read as soft dots. The dashes
 * stay still (in Mapbox GL 3 a changing dash re-lays the line out and stops
 * it drawing); arrowheads move along them instead (see routeArrows).
 */
export const ROUTE_DASH = 2;
export const ROUTE_GAP = 1.6;

interface RoutePoint {
  lat: number | string;
  long: number | string;
}

/** Web Mercator y, so angles match what the north-up map draws. */
const mercatorY = (lat: number): number =>
  Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

/** Ignore wiggles closer than this when reading the direction of travel. */
const HEADING_LOOKAHEAD_KM = 5;

/**
 * Screen direction of travel in degrees clockwise from north (map is locked
 * north-up), from the current position towards the next route point at least
 * HEADING_LOOKAHEAD_KM ahead. Null when the route has nowhere left to go.
 */
export const getRouteHeadingDegrees = (
  points: RoutePoint[] | null | undefined,
  fromIndex: number,
  position: { lat: number; lng: number } | null
): number | null => {
  if (!Array.isArray(points) || !position) return null;
  for (let i = Math.max(0, fromIndex); i < points.length; i += 1) {
    const lat = Number(points[i]?.lat);
    const lng = Number(points[i]?.long);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (
      haversineKm(position.lat, position.lng, lat, lng) < HEADING_LOOKAHEAD_KM
    )
      continue;
    const dx = (lngDelta(position.lng, lng) * Math.PI) / 180;
    const dy = mercatorY(lat) - mercatorY(position.lat);
    const degrees = (Math.atan2(dx, dy) * 180) / Math.PI;
    return (degrees + 360) % 360;
  }
  return null;
};

export interface RouteEndpoint {
  kind: 'origin' | 'destination';
  lat: number;
  lng: number;
  label: string;
  /** The focus circle sits on this place: its name is lifted clear of it. */
  underRing?: boolean;
  /** Where the name is anchored when lifted: the circle's centre. */
  labelAt?: { lat: number; lng: number };
  /** Where the name goes (see EndpointLabelSlot); `dot` when unset. */
  slot?: EndpointLabelSlot;
}

/**
 * A place whose dot is within this many pixels of the focus circle's centre
 * is under the circle (40px across, plus its border and halo).
 */
export const RING_CLEAR_PX = 30;

/**
 * Where an endpoint's name goes. `above`/`below` are around the focus circle
 * (the name anchored at its centre, `labelAt`): 2.2em (33px) clears its ~27px
 * edge wherever under the circle the place is. Both ends under the circle get
 * one slot each (destination above, origin below); the same name twice is
 * shown once (`hidden`). Otherwise the name sits just above its dot.
 */
export type EndpointLabelSlot = 'dot' | 'above' | 'below' | 'hidden';

/** Endpoint label offset (ems at text-size 15) for its slot. */
export const ENDPOINT_LABEL_OFFSET = [
  'match',
  ['get', 'slot'],
  'above',
  ['literal', [0, -2.2]],
  'below',
  ['literal', [0, 2.2]],
  ['literal', [0, -0.8]],
];

/** Endpoint label anchor for its slot: names below the circle hang from it. */
export const ENDPOINT_LABEL_ANCHOR = [
  'match',
  ['get', 'slot'],
  'below',
  'top',
  'bottom',
];

/** Marks the endpoints the focus circle covers on screen right now. */
export const markEndpointsUnderRing = (
  endpoints: RouteEndpoint[],
  ring: { lat: number; lng: number } | null,
  project: (lngLat: [number, number]) => { x: number; y: number }
): RouteEndpoint[] => {
  if (!ring)
    return endpoints.map((e): RouteEndpoint => ({
      ...e,
      underRing: false,
      labelAt: undefined,
      slot: 'dot',
    }));
  const centre = project([ring.lng, ring.lat]);
  const under = endpoints.map((e) => {
    // The world copy next to the circle: across the date line, 179.99 and
    // -179.99 sit side by side on screen, not a world apart.
    const at = project([wrapLngNear(e.lng, ring.lng), e.lat]);
    return Math.hypot(at.x - centre.x, at.y - centre.y) < RING_CLEAR_PX;
  });
  const covered = endpoints.filter((_, i) => under[i]);
  const both = covered.length > 1;
  const sameName =
    both &&
    new Set(covered.map((e) => e.label.trim().toLowerCase())).size === 1;
  return endpoints.map((e, i): RouteEndpoint => {
    if (!under[i])
      return { ...e, underRing: false, labelAt: undefined, slot: 'dot' };
    let slot: EndpointLabelSlot = 'above';
    if (both && e.kind === 'origin') slot = sameName ? 'hidden' : 'below';
    return {
      ...e,
      underRing: true,
      labelAt: { lat: ring.lat, lng: ring.lng },
      slot,
    };
  });
};

interface DetailLocation {
  name?: string | null;
  parsed_address?: { PlaceName?: string | null } | null;
  latlng?: {
    lat?: number | string | null;
    lng?: number | string | null;
  } | null;
}

/**
 * Origin and destination pins from the shipment detail the map already loads
 * (`from_location` / `to_location`), labelled with the city, else the place
 * name. A missing location comes back as 0,0, which counts as no pin.
 */
/** Map labels longer than this are cut with an ellipsis. */
export const ENDPOINT_LABEL_MAX = 18;

const shortLabel = (text: string): string => {
  const trimmed = text.trim();
  return trimmed.length > ENDPOINT_LABEL_MAX
    ? `${trimmed.slice(0, ENDPOINT_LABEL_MAX - 1).trimEnd()}…`
    : trimmed;
};

export const shipmentEndpoints = (
  shipment:
    | {
        from_location?: DetailLocation | null;
        to_location?: DetailLocation | null;
      }
    | null
    | undefined
): RouteEndpoint[] => {
  const ends: [RouteEndpoint['kind'], DetailLocation | null | undefined][] = [
    ['origin', shipment?.from_location],
    ['destination', shipment?.to_location],
  ];
  return ends.flatMap(([kind, location]) => {
    const lat = toCoordinate(location?.latlng?.lat);
    const lng = toCoordinate(location?.latlng?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
    if (lat === 0 && lng === 0) return [];
    // A placeholder name says nothing on a wall TV: keep the dot, no label.
    const name = /^\s*unknown location\s*$/i.test(location?.name || '')
      ? ''
      : location?.name;
    // The city; without one, the place's own name, cut short: a long facility
    // name ("Mount Sinai Medical Center") swamped the spotlight.
    const label =
      location?.parsed_address?.PlaceName || shortLabel(name || '') || '';
    return [{ kind, lat, lng, label }];
  });
};

/** GeoJSON for the origin/destination pins; skips ends without coordinates. */
export const buildRouteEndpointFeatures = (
  endpoints: RouteEndpoint[] | null | undefined
) => ({
  type: 'FeatureCollection' as const,
  features: (endpoints ?? [])
    .filter((e) => Number.isFinite(e?.lat) && Number.isFinite(e?.lng))
    // A dot at the place, and its name at the place or, lifted, at the
    // circle's centre: each layer filters on `role`.
    .flatMap((e) => {
      const properties = {
        kind: e.kind,
        label: e.label,
        underRing: Boolean(e.underRing),
        slot: e.slot ?? 'dot',
      };
      const labelAt = e.underRing && e.labelAt ? e.labelAt : e;
      const dot = {
        type: 'Feature' as const,
        properties: { ...properties, role: 'dot' },
        geometry: { type: 'Point' as const, coordinates: [e.lng, e.lat] },
      };
      if (properties.slot === 'hidden') return [dot];
      return [
        dot,
        {
          type: 'Feature' as const,
          properties: { ...properties, role: 'label' },
          geometry: {
            type: 'Point' as const,
            coordinates: [labelAt.lng, labelAt.lat],
          },
        },
      ];
    }),
});

/**
 * Opacity expression for shipment markers: the one in focus stays solid,
 * everything else steps back. Null focus restores full opacity.
 */
export const monitorDimOpacity = (focusShipmentId: string | null) =>
  focusShipmentId
    ? [
        'case',
        ['==', ['to-string', ['get', 'shipmentId']], focusShipmentId],
        1,
        MONITOR_DIM_OPACITY,
      ]
    : 1;

/** Opacity for cluster bubbles: a cluster is never the shipment in focus. */
export const monitorClusterOpacity = (focusShipmentId: string | null) =>
  focusShipmentId ? MONITOR_DIM_OPACITY : 1;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;
const wrapLng = (lng: number) => ((((lng + 180) % 360) + 360) % 360) - 180;

/**
 * Where the sun is overhead at `date` (low-precision solar position, well
 * under 1° — plenty for a shaded band on a wall map).
 */
export const subsolarPoint = (date: Date): { lat: number; lng: number } => {
  // Days since J2000.0 (2000-01-01 12:00 UTC).
  const d = (date.getTime() - Date.UTC(2000, 0, 1, 12)) / 86400000;
  const meanAnomaly = toRad(357.529 + 0.98560028 * d);
  const meanLongitude = 280.459 + 0.98564736 * d;
  const eclipticLongitude = toRad(
    meanLongitude +
      1.915 * Math.sin(meanAnomaly) +
      0.02 * Math.sin(2 * meanAnomaly)
  );
  const obliquity = toRad(23.439 - 0.00000036 * d);
  const declination = Math.asin(
    Math.sin(obliquity) * Math.sin(eclipticLongitude)
  );
  const rightAscension = Math.atan2(
    Math.cos(obliquity) * Math.sin(eclipticLongitude),
    Math.cos(eclipticLongitude)
  );
  const siderealDegrees = (18.697374558 + 24.06570982441908 * d) * 15;
  return {
    lat: toDeg(declination),
    lng: wrapLng(toDeg(rightAscension) - siderealDegrees),
  };
};

/** Mercator cannot show the poles; stop where the map does. */
const MAX_MERCATOR_LAT = 85;
const NIGHT_STEP_DEG = 2;

/**
 * The night side of the Earth at `date` as one polygon: the terminator from
 * west to east, closed along the pole that is in darkness.
 */
export const buildNightPolygon = (date: Date) => {
  const sun = subsolarPoint(date);
  // At an equinox the terminator runs pole to pole; keep tan() finite.
  const declination =
    Math.abs(sun.lat) < 0.01 ? (sun.lat < 0 ? -0.01 : 0.01) : sun.lat;
  const terminator: [number, number][] = [];
  for (let lng = -180; lng <= 180; lng += NIGHT_STEP_DEG) {
    const lat = toDeg(
      Math.atan(-Math.cos(toRad(lng - sun.lng)) / Math.tan(toRad(declination)))
    );
    terminator.push([
      lng,
      Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat)),
    ]);
  }
  // Northern summer: the south pole is dark, and the other way round.
  const darkPoleLat = declination > 0 ? -MAX_MERCATOR_LAT : MAX_MERCATOR_LAT;
  const ring: [number, number][] = [
    ...terminator,
    [180, darkPoleLat],
    [-180, darkPoleLat],
    terminator[0],
  ];
  return {
    type: 'Feature' as const,
    properties: {},
    geometry: { type: 'Polygon' as const, coordinates: [ring] },
  };
};

interface PaintableMap {
  getLayer: (id: string) => unknown;
  setPaintProperty: (layer: string, name: string, value: unknown) => void;
}

/**
 * Steps every other shipment back while one is in focus (null restores).
 * Safe to call before the cluster layers exist; call again once they do.
 */
export const applyMonitorDim = (
  map: PaintableMap,
  focusShipmentId: string | null
) => {
  const points = monitorDimOpacity(focusShipmentId);
  const clusters = monitorClusterOpacity(focusShipmentId);
  const paint: [string, string, unknown][] = [
    ['shipment-points', 'icon-opacity', points],
    ['shipment-clusters', 'icon-opacity', clusters],
    ['shipment-clusters', 'text-opacity', clusters],
    ['shipment-cluster-alert-badges', 'icon-opacity', clusters],
  ];
  paint.forEach(([layer, name, value]) => {
    if (map.getLayer(layer)) map.setPaintProperty(layer, name, value);
  });
};

/** The spotlight tilts this much, like a seatback map's track-up view. */
export const MONITOR_TILT_PITCH = 30;
/** Highest tilt the monitor map allows (the dashboard stays flat). */
export const MONITOR_MAX_PITCH = 40;
/** The spotlight turns at most this far from north, either way. */
export const MONITOR_MAX_TURN = 45;
/** Only regional views tilt: tilting a world view shows the map's edge. */
export const MONITOR_TILT_MIN_ZOOM = 4;

/**
 * Camera angle for a spotlight: tilted, with the direction of travel up the
 * screen, when the route is regional and the heading is known; otherwise
 * flat and north-up.
 */
export const monitorFocusCamera = (
  heading: number | null,
  fitZoom: number | null
): { pitch: number; bearing: number } => {
  if (heading === null || fitZoom === null || fitZoom < MONITOR_TILT_MIN_ZOOM)
    return { pitch: 0, bearing: 0 };
  // Lean towards the direction of travel, but never past MONITOR_MAX_TURN:
  // a wall map turned upside down (England to Basel) confuses more than it
  // helps. The ring's arrow still shows the exact heading.
  const signed = ((((heading + 180) % 360) + 360) % 360) - 180;
  return {
    pitch: MONITOR_TILT_PITCH,
    bearing: Math.max(-MONITOR_MAX_TURN, Math.min(MONITOR_MAX_TURN, signed)),
  };
};

/** The arrow's angle on screen once the map itself is rotated. */
export const screenHeading = (heading: number, mapBearing: number): number =>
  (((heading - mapBearing) % 360) + 360) % 360;
